/**
 * useUrbanEntityInteractivity — Selección de entidades desde el mapa
 *
 * @remarks
 * Escucha el evento custom 'urban-entity-clicked' que emite useUrbanEntitiesLayer.
 * Al recibir un click, busca la entidad en el array local y actualiza el store.
 * Luego llama a setActivePanel('urban-entity') para abrir el panel derecho.
 *
 * Separado de useUrbanEntitiesLayer para que el array de entidades esté disponible
 * sin inyectarlo en el hook de la capa.
 *
 * @module urban-inventory/hooks
 */

import { useEffect } from 'react';
import { useUrbanInventoryStore } from '../store/urban-inventory-store';
import type { UrbanEntity } from '../types/urban-entity.types';

interface UseUrbanEntityInteractivityOptions {
  entities: UrbanEntity[];
  /** Callback para abrir el panel derecho con 'urban-entity' */
  onEntitySelected: (entity: UrbanEntity) => void;
  /** Callback para cerrar el panel derecho */
  onEntityDeselected: () => void;
}

export function useUrbanEntityInteractivity({
  entities,
  onEntitySelected,
  onEntityDeselected,
}: UseUrbanEntityInteractivityOptions): void {
  const selectEntity = useUrbanInventoryStore((s) => s.selectEntity);

  useEffect(() => {
    const handleEntityClicked = (e: Event): void => {
      const { entityId } = (e as CustomEvent<{ entityId: string }>).detail;
      const entity = entities.find((en) => en.id === entityId) ?? null;

      if (!entity) {
        // Entidad no encontrada en el array local — deseleccionar
        selectEntity(null);
        onEntityDeselected();
        return;
      }

      selectEntity(entity);
      onEntitySelected(entity);
    };

    window.addEventListener('urban-entity-clicked', handleEntityClicked);
    return () => window.removeEventListener('urban-entity-clicked', handleEntityClicked);
  }, [entities, selectEntity, onEntitySelected, onEntityDeselected]);
}
